import { type ReactElement, type ReactNode, cloneElement } from 'react';
import type { PopoverProps, Selection } from 'react-aria-components';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { Button, DialogTrigger, Popover, Tag, TagGroup as RACTagGroup, TagList, Text } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useFieldContext } from '../../contexts';

import { DefaultTagAutocomplete } from './DefaultTagAutocomplete';
import { FormFieldError } from './FormFieldError';

import styles from './TagGroup.module.css';

export interface InlineTagGroupProps<T = string> {
  description?: ReactNode;
  placeholder?: string;
  emptyTrigger?: ReactNode;
  multiTrigger?: (count: number) => ReactNode;
  tagIcon?: React.ComponentProps<typeof Icon>['name'];
  renderAutocomplete?: (props: {
    selectedKeys: Selection;
    onSelectionChange: (keys: Selection) => void;
    placeholder: string;
  }) => ReactNode;
  children?: (item: T) => ReactElement | null;
  getItemKey?: (item: T) => string;
  getItemText?: (item: T) => string;
  placement?: PopoverProps['placement'];
  forceMultiTrigger?: boolean;
  /** When false, tags render without the inline remove (X) button. Defaults to true. */
  allowsRemoving?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export function InlineTagGroup<T = string>({
  description,
  placeholder = 'Search or enter new tags...',
  emptyTrigger,
  multiTrigger,
  tagIcon,
  renderAutocomplete,
  children,
  getItemKey,
  getItemText,
  placement = 'top start',
  forceMultiTrigger,
  allowsRemoving = true,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: InlineTagGroupProps<T>) {
  const field = useFieldContext<T[]>();

  const tags = field.state.value || [];
  const tagCount = tags.length;

  const getKey = (item: T): string => {
    if (typeof item === 'string') {
      return item as string;
    }
    if (getItemKey) {
      return getItemKey(item);
    }
    return String(item);
  };

  const getText = (item: T): string => {
    if (getItemText) {
      return getItemText(item);
    }
    if (typeof item === 'string') {
      return item as string;
    }
    return String(item);
  };

  const selectedKeys = new Set(tags.map(getKey));

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }

    const selectedKeyArray = Array.from(keys).map(String);

    // If no getItemKey is provided, we're dealing with string types since that's the default behavior
    const isStringType = !getItemKey;

    if (isStringType) {
      // For string types, create new strings from the selected keys
      const newTags = selectedKeyArray.map((key) => key as T);
      field.handleChange(newTags);
      return;
    }

    const existingTags = tags.filter((item) => selectedKeyArray.includes(getKey(item)));

    field.handleChange(existingTags);
  };

  // Use custom autocomplete or default
  const autocompleteComponent = renderAutocomplete ? (
    renderAutocomplete({ selectedKeys, onSelectionChange: handleSelectionChange, placeholder })
  ) : (
    <DefaultTagAutocomplete
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      placeholder={placeholder}
      tags={tags.map(getText)}
    />
  );

  // Prepare ARIA attributes for DialogTrigger
  const dialogTriggerAriaProps: { 'aria-label'?: string; 'aria-labelledby'?: string } = {};
  if (ariaLabelledBy) {
    dialogTriggerAriaProps['aria-labelledby'] = ariaLabelledBy;
  } else if (ariaLabel) {
    dialogTriggerAriaProps['aria-label'] = ariaLabel;
  }

  // Render different states based on tag count
  if (tagCount === 0) {
    // No tags - show empty trigger or default add button
    return (
      <div>
        <DialogTrigger {...dialogTriggerAriaProps}>
          {emptyTrigger || (
            <Button variant="minimal" size="small">
              <Icon name="add" size="small" />
              Add tags
            </Button>
          )}

          <Popover placement={placement}>{autocompleteComponent}</Popover>
        </DialogTrigger>

        {description && <Text slot="description">{description}</Text>}
        <FormFieldError meta={field.state.meta} />
      </div>
    );
  }

  if ((tagCount > 1 || forceMultiTrigger) && multiTrigger) {
    // Multiple tags with custom trigger
    return (
      <div>
        <DialogTrigger {...dialogTriggerAriaProps}>
          {multiTrigger(tagCount)}

          <Popover placement={placement}>{autocompleteComponent}</Popover>
        </DialogTrigger>

        {description && <Text slot="description">{description}</Text>}
        <FormFieldError meta={field.state.meta} />
      </div>
    );
  }

  // Default case: show tags as clickable triggers (single or multiple)
  return (
    <div>
      <DialogTrigger {...dialogTriggerAriaProps}>
        <RACTagGroup
          aria-label="Selected tags"
          selectionMode="multiple"
          onRemove={
            allowsRemoving
              ? (keys: 'all' | Set<React.Key>) => {
                  if (keys === 'all') {
                    field.handleChange([]);
                  } else {
                    const keysToRemove = Array.from(keys).map(String);
                    const newTags = tags.filter((item) => !keysToRemove.includes(getKey(item)));
                    field.handleChange(newTags);
                  }
                }
              : undefined
          }
        >
          <TagList
            items={tags.map((item) => ({
              id: getKey(item),
              textValue: getText(item),
              original: item,
            }))}
          >
            {(item: { id: string; textValue: string; original: T }) => {
              if (children) {
                const element = children(item.original);
                return element
                  ? cloneElement(element, {
                      id: item.id,
                      textValue: item.textValue,
                    } as React.ComponentProps<typeof Tag>)
                  : null;
              }

              return (
                <Tag id={item.id} textValue={item.textValue} className={styles.tag}>
                  {tagIcon && <Icon name={tagIcon} size="small" />}
                  <TextTruncator>{item.textValue}</TextTruncator>
                </Tag>
              );
            }}
          </TagList>
        </RACTagGroup>

        <Popover placement={placement}>{autocompleteComponent}</Popover>
      </DialogTrigger>

      {description && <Text slot="description">{description}</Text>}
      <FormFieldError meta={field.state.meta} />
    </div>
  );
}
